import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useTrigerrableEffect from "react-triggerable-effect";
import FindingAPI from "restAPI/FindingAPI";
import {
  FINDING_ACTIONS,
  FINDING_STATUS,
  FINDING_STATUS_POST_ACTION_TEXT,
} from "../Constants";
import { getFindingStatusApiType, isCustomFinding } from "../utils";
import message from "components/CustomMessage";
import FalsePositivePopup from "../FalsePositivePopup";
import AcceptRiskPopup from "../AcceptRiskPopup";
import ConfirmedPopup from "../ConfirmedPopup";
import InProgressStatusPopup from "../InProgressStatusPopup";
import ChangeOwner from "../ChangeOwner";
import SuppressedPopup from "../SuppressedPopup";
import TriagePopup from "../TriagePopup";
import ModalUtil from "utils/modalUtil";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import FindingFieldEditModal from "../FindingFieldEditModal";
import _, { forEach, isEmpty, noop } from "lodash";
import FindingDueDateForm from "../FindingDueDateForm";
import SeverityCategoryChangeModal from "../SeverityCategoryChangeModal";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";
import FeedsAPI from "views/Feeds/FeedsAPI";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { showAPIError } from "restAPI";
import FindingsBasicForm from "../FindingsBasicForm";
import AssetsAPI from "restAPI/AssetAPI";
import CustomFindingFieldEditModal from "../CustomFindingFieldEditModal";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import {
  getAnyaAssistant,
  getAnyaConversations,
  getAnyaMessage,
} from "restAPI/AnyaAPI";
import moment from "moment-timezone";
import { ANYA_PROMPTS, ANYA_POLLING_CONFIG } from "views/Dashboards/anyaConstants";
import { sha256Hash, isStatusInProgress, isResponseCompleted } from "views/Dashboards/anyaUtils";
import useModalRenderer from "hooks/useModalRenderer";
import NewCVSSCalculator from "components/cvss/NewCVSSCalculator";
import { extractCVSSVersion, validateCVSSVectorFormat } from "utils/cvssUtil";

const FindingDetailsContext = React.createContext({
  history: undefined,
  bucket: undefined,
  finding: undefined,
  loading: false,
  fetchFinding: noop,
  updateFindingDetails: noop,
  id: undefined,
  onUpdate: noop,
  showFindingActionPopup: noop,
  openFindingFieldEditPopup: noop,
  findingTagsRef: {},
  isCustomFinding: false,
  showCVSSVectorComponents: noop,
  showDueDatePopup: noop,
  handleArmorcodeSeverityChange: noop,
  handleArmorcodeCategoryChange: noop,
  ticketDetails: undefined,
  fetchTickets: noop,
  cveDetails: undefined,
  showBasicEdit: noop,
  findingNavigatorProps: {},
  onFindingIdChange: noop,
  openCustomFindingFieldEditPopup: noop,
  existingConversations: [],
  findingCorrelations: [],
  // AI Summary state
  aiSummaryResponse: null,
  aiSummaryLoading: false,
  aiSummaryError: null,
  aiSummaryExists: true,
});

export default function FindingDetailsProvider({
  children,
  history,
  bucket,
  id,
  updateGlobalFilters,
  mainModalRef,
  findingNavigatorProps = {},
}) {
  const [findingId, setFindingId] = useState(id);
  const [finding, setFinding] = useState();
  const [ticketDetails, setTicketDetails] = useState();
  const [loading, setLoading] = useState(true);
  const featureFlags = useFeatureFlag();
  const findingTagsRef = useFeatureFlag();
  const [cveDetails, setCveDetails] = useState();
  const [ctiScoreCveDetails, setCtiScoreCveDetails] = useState();
  const [assetDetails, setAssetDetails] = useState([]);
  const activeUser = useActiveUserContext();
  const [findingCustomFields, setFindingCustomFields] = useState();
  const [existingConversations, setExistingConversations] = useState([]);
  const [findingCorrelations, setFindingCorrelations] = useState([]);
  const { anyaAIAssistantEnabled: isAnyaGloballyEnabled, aiSummaryEnabled } = useGlobalSettingsContext();

  // AI Summary state
  const [aiSummaryResponse, setAISummaryResponse] = useState(null);
  const [aiSummaryLoading, setAISummaryLoading] = useState(false);
  const [aiSummaryError, setAISummaryError] = useState(null);
  const [aiSummaryExists, setAISummaryExists] = useState(true);
  const modalRenderer = useModalRenderer();
  const aiSummaryPrefetchCacheRef = useRef({});

  useEffect(() => {
      const { promise, cancel } = GlobalSettingAPI.getFindingCustomFields();
      promise.then(({ findingCustomFields }) => {
        setFindingCustomFields(findingCustomFields);
      });
      return cancel;
  }, []);

  const onFindingIdChange = useCallback((id) => {
    setFindingId(id);
  }, []);

  useEffect(() => {
    if (!featureFlags.AATI_SCORE_COLUMN) {
      return;
    }
    let promise, cancel;
    if (finding !== undefined && finding.additionalDetails?.cveUsed) {
      const request = FeedsAPI.getFeedDetails(
        "CSW",
        _.get(finding, "additionalDetails.cveUsed")
      );
      promise = request.promise;
      cancel = request.cancel;
    } else {
      promise = Promise.resolve({});
      cancel = noop;
    }
    promise.then(setCveDetails);
    return cancel;
  }, [finding, featureFlags.AATI_SCORE_COLUMN]);

  // Background Anya API Remediation Effect
  useEffect(() => {
    if (featureFlags.AI_ANYA_AGENT) {
      return;
    }
    if (finding?.id && featureFlags.AI_REMEDIATION_AUTO_LOAD && !featureFlags.AI_ANYA_REMEDIATION_ACTIONS_DISABLED && featureFlags.AI_ANYA_REMEDIATION_ACTIONS && isAnyaGloballyEnabled) {
      // Check if there is existing conversation for this finding before calling assistant API
      const checkConversationsAndCallAssistant = async () => {        
        try {          
          const currentWidgetId = await sha256Hash(`${finding.id}${finding.id}`);

          const conversationsResponse = await getAnyaConversations({ 
            widgetId: currentWidgetId 
          }).promise;
          const conversations = conversationsResponse?.data?.history || [];
          setExistingConversations(conversations);
          if (isEmpty(conversations)) {            
            // No conversation found, send assistant API call to generate remediation
            getAnyaAssistant({
              query: `${ANYA_PROMPTS.FINDING_REMEDIATION}${finding.id}`,
              conversation_history: [],
              widgetTitle: "",
              widgetType: String(finding.id),
              dashboardId: String(finding.id),
              locale: navigator.language || "en-US",
              zoneId: moment.tz.guess(),
            });
          }
          // If conversation exists, all good - it will be fetched by the widget
        } catch (conversationError) {
          console.error('Error checking conversations', conversationError);
        }
      };

      checkConversationsAndCallAssistant();
    }
  }, [finding?.id, featureFlags.AI_ANYA_AGENT, featureFlags.AI_REMEDIATION_AUTO_LOAD, featureFlags.AI_ANYA_REMEDIATION_ACTIONS, featureFlags.AI_ANYA_REMEDIATION_ACTIONS_DISABLED, isAnyaGloballyEnabled]);

  // Builds the payload for getAnyaAssistant AI summary requests.
  const buildAISummaryPayload = (findingId) => ({
    query: `${ANYA_PROMPTS.FINDING_SUMMARY}${findingId}`,
    conversation_history: [],
    widgetTitle: "AI_Summary",
    widgetType: String(findingId),
    dashboardId: String(findingId),
    locale: navigator.language || "en-US",
    zoneId: moment.tz.guess(),
  });

  // Handles a completed AI summary response — sets state or marks as non-existent.
  const handleAISummaryComplete = (data) => {
    const hasContent = data?.reasoningSummary || data?.details;
    if (hasContent) {
      setAISummaryResponse(data);
      setAISummaryExists(true);
    } else {
      setAISummaryResponse(null);
      setAISummaryExists(false);
    }
    setAISummaryLoading(false);
  };

  // AI Summary fetch effect - loads when finding opens and feature is enabled
  useEffect(() => {
    if (!finding?.id || !aiSummaryEnabled || featureFlags.AI_ANYA_AGENT) {
      return;
    }

    // Reset state immediately — prevents showing previous finding's summary
    setAISummaryResponse(null);
    setAISummaryError(null);
    setAISummaryExists(true);
    setAISummaryLoading(true);

    let pollTimeoutId = null;
    let isCancelled = false;
    const pollingStartTime = Date.now();

    // Polling function — uses getAnyaMessage (not getAnyaAssistant) to retrieve
    // the AI summary result by requestId. getAnyaAssistant starts generation,
    // getAnyaMessage fetches the result. For cached (prefetched) findings this
    // avoids a duplicate getAnyaAssistant call; for non-cached findings it polls
    // until the in-progress generation completes.
    const pollForResponse = async (conversationId, requestId) => {
      if (isCancelled) return;

      // Check timeout
      if (Date.now() - pollingStartTime > ANYA_POLLING_CONFIG.MAX_POLL_TIME) {
        setAISummaryError("Request timed out. Please try again.");
        setAISummaryLoading(false);
        return;
      }

      try {
        const { promise } = getAnyaMessage({ conversationId, messageId: requestId });
        const result = await promise;
        const data = result?.data || result;

        if (isCancelled) return;

        if (isResponseCompleted(data)) {
          handleAISummaryComplete(data);
        } else {
          // Continue polling
          pollTimeoutId = setTimeout(() => {
            pollForResponse(conversationId, requestId);
          }, ANYA_POLLING_CONFIG.SUBSEQUENT_POLL_INTERVAL);
        }
      } catch {
        if (!isCancelled) {
          setAISummaryError("Failed to load AI summary");
          setAISummaryLoading(false);
        }
      }
    };

    // Check if we have a cached requestId from prefetch
    const cached = aiSummaryPrefetchCacheRef.current[finding.id];

    if (cached) {
      // Backend already started generating the summary via prefetch.
      // Use the cached requestId to poll for the completed response.
      pollTimeoutId = setTimeout(() => {
        pollForResponse(cached.conversationId, cached.requestId);
      }, ANYA_POLLING_CONFIG.INITIAL_POLL_DELAY);

      return () => {
        isCancelled = true;
        if (pollTimeoutId) {
          clearTimeout(pollTimeoutId);
        }
      };
    }
    // No cache — call getAnyaAssistant to generate the AI summary
    const { promise, cancel } = getAnyaAssistant(buildAISummaryPayload(finding.id));

    promise
      .then(res => {
        if (isCancelled) return;

        const response = res?.data || res;
        const { requestId, conversationId, reasoningSummary, details } = response;

        // Check if response is already completed (not in progress)
        if (response?.status && !isStatusInProgress(response)) {
          handleAISummaryComplete(response);
        } else if (requestId && conversationId) {
          // Start polling after initial delay
          pollTimeoutId = setTimeout(() => {
            pollForResponse(conversationId, requestId);
          }, ANYA_POLLING_CONFIG.INITIAL_POLL_DELAY);
        } else {
          setAISummaryError("Invalid response from AI service");
          setAISummaryLoading(false);
        }
      })
      .catch(err => {
        if (!isCancelled && !err.cancelled) {
          setAISummaryError("Failed to load AI summary");
        }
        setAISummaryLoading(false);
      });

    return () => {
      isCancelled = true;
      cancel();
      if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
      }
    };
  }, [finding?.id, aiSummaryEnabled, featureFlags.AI_ANYA_AGENT]);

  // Trigger backend AI summary generation for the next finding and cache the
  // requestId. When the user navigates to that finding, the main useEffect
  // uses the cached requestId to poll for the already-generated summary.
  const onPrefetchNext = useCallback((entityId) => {
    if (!aiSummaryEnabled || featureFlags.AI_ANYA_AGENT) return;
    getAnyaAssistant(buildAISummaryPayload(entityId)).promise
      .then((res) => {
        const response = res?.data || res;
        if (response?.requestId && response?.conversationId) {
          aiSummaryPrefetchCacheRef.current[entityId] = {
            requestId: response.requestId,
            conversationId: response.conversationId,
          };
        }
      })
      .catch(() => {});
  }, [aiSummaryEnabled, featureFlags.AI_ANYA_AGENT]);

  // Augment navigator props with prefetch callback so the navigator
  // triggers AI summary generation for the next finding after each navigation.
  const augmentedNavigatorProps = useMemo(() => {
    if (!findingNavigatorProps.api) return findingNavigatorProps;
    return { ...findingNavigatorProps, onPrefetchNext };
  }, [findingNavigatorProps, onPrefetchNext]);

  //get feed details for Mandiant Advantage
  useEffect(() => {
    let promise, cancel;
    if (finding !== undefined && finding.additionalDetails?.threatSource === "Mandiant Advantage" && finding.additionalDetails?.cveUsed) {
      const request = FeedsAPI.getFeedDetails(
        "MANDIANT_ADVANTAGE",
        _.get(finding, "additionalDetails.cveUsed")
      );
      promise = request.promise;
      cancel = request.cancel;
    } else {
      promise = Promise.resolve({});
      cancel = noop;
    }
    promise.then(setCtiScoreCveDetails);
    return cancel;
  }, [finding]);

  const fetchFinding = useTrigerrableEffect(() => {
    setLoading(true);
    const { cancel, promise } = FindingAPI.get(findingId, false);
    const { cancel: cancelCorrelated, promise: correlatedPromise } = FindingAPI.getCorrelatedFindings(findingId, {
      filters: {}
    });
    let cancel2;

    correlatedPromise.then(res => {
      setFindingCorrelations(res);
    }).catch(() => {
      setFindingCorrelations([]);
    });

    promise.then(res => {
      const customFieldsData = {};
      if(!isEmpty(res.customFields)){
        forEach(res.customFields, (field) => customFieldsData[field.fieldId] = field.fieldValue);
      }
      const finalResponse = { ...res, ...customFieldsData };
      if(res.additionalDetails.descriptionFileUpload){
        const { promise: promise2, cancel } = FindingAPI.getFindingDescription(findingId);
        cancel2 = cancel;
        promise2.then(des => {
          setFinding({...finalResponse, description: des});
          setLoading(false);
        })
      } else {
        setFinding({ ...finalResponse });
        setLoading(false);
      }
    })
    .catch(e => {
      if (e.response.data.errorCode === "MOVE_TO_ANOTHER_BU") {
        const buId = e.response.data.message;
          if (updateGlobalFilters) {
            message.warn("Finding moved to another organization, switching organization.")
            updateGlobalFilters({
              businessUnitId: parseInt(buId),
              callback: () => {
                  history.replace(`/u/${activeUser.tenantId}/${buId}/findings/${findingId}`);
                  window.location.reload();
              }
          });
        }
      } else {
        showAPIError(e);
      }
    })
    return () => { cancel(); cancelCorrelated(); _.isFunction(cancel2) && cancel2();};

  }, [findingId]);

  const updateFindingDetails = useCallback((updatedData) => {
    setFinding((prevData) => ({ ...prevData, ...updatedData }));
  }, []);

  const fetchTickets = useTrigerrableEffect(() => {
    if (!finding) {
      return;
    }
    setTicketDetails(undefined);
    const { cancel, promise } = TicketsCreationAPI.getJiraTicketDetails(
      finding.id
    );
    promise.then(setTicketDetails);
    return cancel;
  }, [finding]);

  const fetchAssetDetails = useCallback((armorcodeAssetId) => {
    const { promise, cancel } =  AssetsAPI.getAssetDetails(armorcodeAssetId);
    promise.then(setAssetDetails).catch((err) => console.error(err));
    return cancel;
  }, []);

  useEffect(() => {
    if(featureFlags.RBVM_ASSETS && finding?.armorcodeAssetId){
      fetchAssetDetails(finding.armorcodeAssetId);
    }
  }, [fetchAssetDetails, featureFlags.RBVM_ASSETS, finding?.armorcodeAssetId]);

  const onUpdate = useCallback(
    (updated, closeSelfPopup, modalRef) => {
      if (updated) {
        if (closeSelfPopup) {
          modalRef.current.close();
        } else {
          fetchFinding();
          if (featureFlags.RBVM_ASSETS && finding?.armorcodeAssetId) {
            fetchAssetDetails(finding.armorcodeAssetId);
          }
        }
      }
    },
    [fetchFinding, fetchAssetDetails, featureFlags.RBVM_ASSETS, finding?.armorcodeAssetId]
  );

  const handleArmorcodeSevirityCategoryChange = useCallback(
    (value, type) => {
      if (_.lowerCase(finding.severity) !== _.lowerCase(value)) {
        ModalUtil.show({
          content: (
            <SeverityCategoryChangeModal
              finding={finding}
              toolName={finding.toolName}
              scanType={finding.scanType}
              findingId={finding.id}
              type={type}
              severity={type === "severity" ? _.upperCase(value) : null}
              category={type === "category" ? value : null}
              history={history}
              oldValue={
                type === "severity" ? finding.severity : finding.category
              }
              findingSeverity={finding.severity}
              findingCategory={finding.category}
              status={finding.status}
              featureFlags={featureFlags}
              tags={finding.tags}
            />
          ),
          afterClose: () => onUpdate(true),
        });
      }
    },
    [history, onUpdate, finding, featureFlags]
  );

  const handleArmorcodeSeverityChange = useCallback(
    (e) => {
      handleArmorcodeSevirityCategoryChange(e.key, "severity");
    },
    [handleArmorcodeSevirityCategoryChange]
  );

  const handleArmorcodeCategoryChange = useCallback(
    async (e) => {
      handleArmorcodeSevirityCategoryChange(e.key, "category");
    },
    [handleArmorcodeSevirityCategoryChange]
  );

  const showFindingActionPopup = useCallback(
    (action) => {
      let Popup;

      // Handle Finding status updates
      const handleFindingAction = async (
        data,
        length,
        similarFindingsToNotUpdate
      ) => {
        if (data.note && !data.notes) {
          data.notes = data.note;
        }
        if (!data.status && FINDING_ACTIONS.CHANGE_OWNER === action) {
          data.status = FINDING_STATUS.CHANGE_OWNER;
        }
        let successMessage, errorMessage, exceptionType;
        try {
          if (
            data.scope !== "FINDING" &&
            action !== FINDING_ACTIONS.CHANGE_OWNER
          ) {
            await FindingAPI.similarFindings.update(
              _.omit(data, ["id", "file"]),
              getFindingStatusApiType(data.status)
            ).promise;
          } else {
            await FindingAPI.updateFindingStatus[_.toLower(data.status)](
              finding.id,
              _.omit(data, ["id", "file"]),
              false
            ).promise;
          }
          // const _finding = await fetchFinding(false);
          if (FINDING_ACTIONS.CHANGE_OWNER === action) {
            message.success(<>Finding ID {finding.id} {FINDING_STATUS_POST_ACTION_TEXT[data.status]}.</>);
          } else {
            successMessage = length ? (
              <>
                ({length - similarFindingsToNotUpdate?.length || 0}) findings
                will be {FINDING_STATUS_POST_ACTION_TEXT[data.status]}. Please
                check after some time
              </>
            ) : (
              <>
                Finding ID {finding.id}{" "}
                {![
                  FINDING_STATUS.FALSE_POSITIVE,
                  FINDING_STATUS.ACCEPT_RISK,
                  FINDING_STATUS.SUPPRESSED,
                  FINDING_STATUS.TRIAGE,
                  FINDING_STATUS.IN_PROGRESS,
                  FINDING_STATUS.CONTROLLED,
                ].includes(data.status) && "status is changed to"}{" "}
                {FINDING_STATUS_POST_ACTION_TEXT[data.status]}
              </>
            );
            if (
              action === FINDING_ACTIONS.FALSEPOSITIVE ||
              action === FINDING_ACTIONS.NOT_FALSEPOSITIVE
            ) {
              message.success(successMessage);
            }
          }
        } catch (e) {
          if(e.response?.status === 409){
            errorMessage = e.response?.data?.message;
            exceptionType = e.response?.data?.exceptionType;
          }
          console.error(e);
        }
        return { successMessage, errorMessage, exceptionType }
      };

      switch (action) {
        case FINDING_ACTIONS.FALSEPOSITIVE:
        case FINDING_ACTIONS.NOT_FALSEPOSITIVE:
          Popup = FalsePositivePopup;
          break;
        case FINDING_ACTIONS.ACCEPT_RISK:
        case FINDING_ACTIONS.REJECT_RISK:
        case FINDING_ACTIONS.CONTROLLED:
          Popup = AcceptRiskPopup;
          break;
        case FINDING_ACTIONS.CONFIRMED:
        case FINDING_ACTIONS.NOT_CONFIRMED:
          Popup = ConfirmedPopup;
          break;
        case FINDING_ACTIONS.IN_PROGRESS:
        case FINDING_ACTIONS.NOT_IN_PROGRESS:
          Popup = InProgressStatusPopup;
          break;
        case FINDING_ACTIONS.CHANGE_OWNER:
          Popup = ChangeOwner;
          break;
        case FINDING_ACTIONS.SUPPRESSED:
        case FINDING_ACTIONS.NOT_SUPPRESSED:
          Popup = SuppressedPopup;
          break;
        case FINDING_ACTIONS.TRIAGE:
        case FINDING_ACTIONS.NOT_TRIAGE:
          Popup = TriagePopup;
          break;
        default:
          break;
      }

      ModalUtil.show({
        content: (
          <Popup
            finding={finding}
            ticketDetails={{}}
            action={action}
            onOk={handleFindingAction}
            history={history}
            featureFlags={featureFlags}
            bucket={bucket}
          />
        ),
        afterClose: (shouldUpdate = true) => onUpdate(shouldUpdate),
      });
    },
    [featureFlags, onUpdate, finding, bucket, history]
  );

  const openFindingFieldEditPopup = useCallback(
    (type) => {
      if(type === "cvssVector"){
        const onSubmit = async ({
          vectorString,
          baseScore,
          note
        }) => {
          await FindingAPI.updateFindingField(finding.id, "findingCvssVector", {
            cvssVector: vectorString,
            cvssScore: baseScore,
            notes: note,
          }).promise;
          fetchFinding();
        }
        const isNotCustomFindingWith3v1Version = extractCVSSVersion(finding?.cvssVector) === "3.1" && !isCustomFinding(finding?.testType);
        modalRenderer.show({
          content: (
            <NewCVSSCalculator
              initialCvssVector={validateCVSSVectorFormat(finding.cvssVector) ? finding.cvssVector : ""}
              selectedCVE={finding.additionalDetails?.cveUsed}
              onSubmit={onSubmit}
              editableModifiersFor3v1={isNotCustomFindingWith3v1Version}
              viewMode={isNotCustomFindingWith3v1Version}
              finding={finding}
            />
          ),
          width: 1056,
        })
        return;
      }
      ModalUtil.show({
        content: (
          <FindingFieldEditModal
            isCustomFinding={isCustomFinding(finding?.testType)}
            finding={finding}
            featureFlags={featureFlags}
            history={history}
            fieldType={type}
          />
        ),
        afterClose: fetchFinding,
      });
    },
    [finding, featureFlags, history, fetchFinding, modalRenderer]
  );

  const openCustomFindingFieldEditPopup = useCallback(
    (customField) => {
      ModalUtil.show({
        content: (
          <CustomFindingFieldEditModal
            finding={finding}
            customField={customField}
          />
        ),
        afterClose: fetchFinding,
      });
    },
    [finding, fetchFinding]
  );

  const showCVSSVectorComponents = useCallback(() => {
    modalRenderer.show({
      content: (
        <NewCVSSCalculator
          initialCvssVector={validateCVSSVectorFormat(finding.cvssVector) ? finding.cvssVector : ""}
          selectedCVE={finding.additionalDetails?.cveUsed}
          viewMode
          finding={finding}
        />
      ),
      width: 1056,
    });
  }, [finding, modalRenderer]);

  const showDueDatePopup = useCallback(() => {
    ModalUtil.show({
      content: <FindingDueDateForm finding={finding} />,
      afterClose: onUpdate,
    });
  }, [onUpdate, finding]);


  const showBasicEdit = useCallback(
    () => {
      ModalUtil.show({
        content: <FindingsBasicForm finding={finding} />,
        afterClose: (...arg) => onUpdate(...arg, mainModalRef),
      });
    },
    [finding, onUpdate, mainModalRef]
  );


  const contextValue = useMemo(() => {
    return {
      history,
      bucket,
      onUpdate,
      showFindingActionPopup,
      openFindingFieldEditPopup,
      finding,
      loading,
      id: findingId,
      findingTagsRef,
      fetchFinding,
      updateFindingDetails,
      isCustomFinding: isCustomFinding(finding?.testType),
      showCVSSVectorComponents,
      showDueDatePopup,
      handleArmorcodeSeverityChange,
      handleArmorcodeCategoryChange,
      ticketDetails,
      fetchTickets,
      cveDetails,
      assetDetails,
      showBasicEdit,
      findingNavigatorProps: augmentedNavigatorProps,
      onFindingIdChange,
      ctiScoreCveDetails,
      openCustomFindingFieldEditPopup,
      findingCustomFields,
      existingConversations,
      findingCorrelations,
      // AI Summary state
      aiSummaryResponse,
      aiSummaryLoading,
      aiSummaryError,
      aiSummaryExists,
    };
  }, [
    history,
    bucket,
    onUpdate,
    showFindingActionPopup,
    openFindingFieldEditPopup,
    finding,
    loading,
    findingId,
    findingTagsRef,
    fetchFinding,
    updateFindingDetails,
    showCVSSVectorComponents,
    showDueDatePopup,
    handleArmorcodeSeverityChange,
    handleArmorcodeCategoryChange,
    ticketDetails,
    fetchTickets,
    cveDetails,
    assetDetails,
    showBasicEdit,
    augmentedNavigatorProps,
    onFindingIdChange,
    ctiScoreCveDetails,
    openCustomFindingFieldEditPopup,
    findingCustomFields,
    existingConversations,
    findingCorrelations,
    aiSummaryResponse,
    aiSummaryLoading,
    aiSummaryError,
    aiSummaryExists,
  ]);

  return (
    <FindingDetailsContext.Provider value={contextValue}>
      {modalRenderer.context}
      {children}
    </FindingDetailsContext.Provider>
  );
}

export function useFindingDetails() {
  return useContext(FindingDetailsContext);
}
